import i18nInstance from 'i18n/i18nInstance'
import { paths } from '@mc/paths'
import { NavigationEvents, Product as TrackProduct } from '@mc/track-event'
import { trackBrowseSkillsCatalogCTAClick } from 'components/Navigation/enterprise/utils'
import {
  trackNavClick,
  trackPlansClick,
  trackGiftCTAClick,
} from 'components/Navigation/events'
import { type Product } from './components/AccordionMenu'

export const productTypeTrackingMap = {
  consumer: TrackProduct.MASTERCLASS,
  certificates: TrackProduct.CERTIFICATES,
  enterprise: TrackProduct.AT_WORK,
} as const

export const getProducts = ({
  pathname,
  query,
  newForBusinessPageEnabled,
  Callout,
  onShowCertificatesSection,
  productType,
  onCallLinksEnabled = false,
}: {
  pathname: string
  query: Record<string, string>
  newForBusinessPageEnabled: boolean
  Callout?: React.ReactNode
  onShowCertificatesSection?: () => void
  productType: 'consumer' | 'enterprise' | 'certificates'
  onCallLinksEnabled?: boolean
}): Product[] => {
  const consumerLinks = [
    {
      title: i18nInstance.t('nav.cta.plans', {
        ns: '@mc/next',
      }),
      href: paths.checkout,
      onClick: trackPlansClick(NavigationEvents.LEVELS.TERTIARY),
      promo: true, // pen-5950_v1_gifting_on_mobile_nav
    },
    {
      title: i18nInstance.t('nav.cta.gift', {
        ns: '@mc/next',
      }),
      href: paths.gift,
      onClick: () => trackGiftCTAClick(NavigationEvents.LEVELS.TERTIARY),
    },
    {
      title: i18nInstance.t('nav.cta.support', {
        ns: '@mc/next',
      }),
      href: paths.helpCenter,
      onClick: trackNavClick(
        i18nInstance.t('nav.cta.support', {
          ns: '@mc/next',
        }),
        NavigationEvents.LEVELS.TERTIARY,
      ),
    },
    {
      title: i18nInstance.t('nav.cta.login', {
        ns: '@mc/next',
      }),
      href: paths.logIn(),
      onClick: trackNavClick(
        i18nInstance.t('nav.cta.login', {
          ns: '@mc/next',
        }),
        NavigationEvents.LEVELS.TERTIARY,
      ),
    },
  ]

  const certificateLinks = [
    {
      title: i18nInstance.t('nav.cta.browseAllCertificates', {
        ns: '@mc/next',
      }),
      href: productType === 'certificates' ? undefined : paths.certificates,
      onClick: () => {
        const trackingFunction = trackNavClick(
          i18nInstance.t('nav.cta.browseAllCertificates', {
            ns: '@mc/next',
          }),
          NavigationEvents.LEVELS.TERTIARY,
          undefined,
          productTypeTrackingMap[productType],
        )

        trackingFunction()

        if (productType === 'certificates') {
          onShowCertificatesSection?.()
        }
      },
    },
    {
      title: i18nInstance.t('nav.cta.login', {
        ns: '@mc/next',
      }),
      href: paths.logIn(),
      onClick: trackNavClick(
        i18nInstance.t('nav.cta.login', {
          ns: '@mc/next',
        }),
        NavigationEvents.LEVELS.TERTIARY,
        undefined,
        productTypeTrackingMap[productType],
      ),
    },
  ]

  const enterpriseLinks = [
    ...(newForBusinessPageEnabled
      ? [
          {
            title: i18nInstance.t('nav.cta.howItWorks', {
              ns: '@mc/next',
            }),
            href: paths.enterpriseHowItWorks,
            onClick: trackNavClick(
              i18nInstance.t('nav.cta.howItWorks', {
                ns: '@mc/next',
              }),
              NavigationEvents.LEVELS.TERTIARY,
            ),
          },
        ]
      : []),
    {
      title: i18nInstance.t('nav.cta.browseSkillsLibrary', {
        ns: '@mc/next',
      }),
      href: paths.enterpriseSkillsLibrary,
      onClick: trackBrowseSkillsCatalogCTAClick({
        action: i18nInstance.t('nav.cta.browseSkillsLibrary', {
          ns: '@mc/next',
        }),
        level: NavigationEvents.LEVELS.TERTIARY,
        pathname,
        query,
      }),
    },
    {
      title: i18nInstance.t('nav.cta.support', {
        ns: '@mc/next',
      }),
      href: paths.helpCenter,
      onClick: trackNavClick(
        i18nInstance.t('nav.cta.support', {
          ns: '@mc/next',
        }),
        NavigationEvents.LEVELS.TERTIARY,
      ),
    },
    {
      title: i18nInstance.t('nav.cta.login', {
        ns: '@mc/next',
      }),
      href: paths.logIn(),
      onClick: trackNavClick(
        i18nInstance.t('nav.cta.login', {
          ns: '@mc/next',
        }),
        NavigationEvents.LEVELS.TERTIARY,
      ),
    },
  ]

  const onCallLinks = [
    ...(onCallLinksEnabled
      ? [
          {
            title: i18nInstance.t('nav.cta.exploreOnCall', {
              ns: '@mc/next',
            }),
            href: paths.onCall,
            onClick: trackNavClick(
              i18nInstance.t('nav.cta.exploreOnCall', {
                ns: '@mc/next',
              }),
              NavigationEvents.LEVELS.TERTIARY,
            ),
          },
          {
            title: i18nInstance.t('nav.cta.login', {
              ns: '@mc/next',
            }),
            href: paths.onCallLogin,
            onClick: trackNavClick(
              i18nInstance.t('nav.cta.login', {
                ns: '@mc/next',
              }),
              NavigationEvents.LEVELS.TERTIARY,
            ),
          },
        ]
      : []),
  ]

  return [
    {
      title: i18nInstance.t('nav.cta.masterClass', {
        ns: '@mc/next',
      }),
      type: 'consumer',
      links: consumerLinks,
      callout: productType === 'consumer' ? Callout : undefined,
      separator: productType === 'consumer' && !!Callout,
      onClick: trackNavClick(
        i18nInstance.t('nav.cta.masterClass', {
          ns: '@mc/next',
        }),
        NavigationEvents.LEVELS.SECONDARY,
      ),
    },
    {
      title: i18nInstance.t('nav.cta.certificates', {
        ns: '@mc/next',
      }),
      type: 'certificates',
      links: certificateLinks,
      callout: productType === 'certificates' ? Callout : undefined,
      separator: productType === 'certificates' && !!Callout,
      onClick: trackNavClick(
        i18nInstance.t('nav.cta.certificates', {
          ns: '@mc/next',
        }),
        NavigationEvents.LEVELS.SECONDARY,
        undefined,
        productTypeTrackingMap[productType],
      ),
    },
    ...(onCallLinksEnabled
      ? [
          {
            title: i18nInstance.t('nav.cta.onCall', {
              ns: '@mc/next',
            }),
            type: 'onCall' as const,
            links: onCallLinks,
            onClick: trackNavClick(
              i18nInstance.t('nav.cta.onCall', {
                ns: '@mc/next',
              }),
              NavigationEvents.LEVELS.SECONDARY,
              undefined,
              productTypeTrackingMap[productType],
            ),
          },
        ]
      : []),
    {
      title: i18nInstance.t('nav.cta.work', {
        ns: '@mc/next',
      }),
      type: 'enterprise',
      links: enterpriseLinks,
      callout: productType === 'enterprise' ? Callout : undefined,
      separator: productType === 'enterprise' && !!Callout,
      onClick: trackNavClick(
        i18nInstance.t('nav.cta.work', {
          ns: '@mc/next',
        }),
        NavigationEvents.LEVELS.SECONDARY,
        undefined,
        productTypeTrackingMap[productType],
      ),
    },
  ]
}
